import { useEffect, useState } from "react";
import type {
  Article,
  Category,
  SearchablePage,
  SearchResultItem,
  SupportSearchResults,
} from "@core/features/support";
import { Dialog } from "@themes/pollinator/ui/Dialog";
import { Input } from "@themes/pollinator/ui/Input";
import { packSupportSearchResults } from "@core/features/support";
import { SupportSearchFormResults } from "./SupportSearchFormResults";

export type Props = {
  articles: Article[];
  categories: Category[];
  pages: SearchablePage[];
  isOpen: boolean;
  onClose: () => void;
  onResultClick: (item: SearchResultItem) => void;
};

export function SupportSearchForm({
  articles,
  categories,
  pages,
  isOpen,
  onClose,
  onResultClick,
}: Props) {
  const [searchQuery, setSearchQuery] = useState("");

  const [results, setResults] = useState<SupportSearchResults>({});

  useEffect(() => {
    if (!searchQuery.trim()) {
      setResults({});
      return;
    }

    const query = searchQuery.toLowerCase();

    // Filter articles by title and excerpt
    const matchedArticles = articles.filter(
      (article) =>
        article.title.toLowerCase().includes(query) ||
        article.excerpt?.toLowerCase().includes(query)
    );

    // Group articles by category (articles appear first)
    const groupedResults = packSupportSearchResults(
      matchedArticles,
      categories
    );

    // Filter pages by title and description
    const matchedPages = pages.filter(
      (page) =>
        page.title.toLowerCase().includes(query) ||
        page.description.toLowerCase().includes(query)
    );

    // Add pages as separate category (appears after articles)
    if (matchedPages.length > 0) {
      groupedResults["Pages"] = matchedPages;
    }

    setResults(groupedResults);
  }, [searchQuery, articles, categories, pages]);

  return (
    <Dialog
      title="Search"
      description="Search through help articles and pages."
      isOpen={isOpen}
      onClose={onClose}
      isWithActions={false}
      primaryButtonText="Search"
      secondaryButtonText="Cancel"
      onPrimaryClick={() => {}}
      onSecondaryClick={() => {}}
    >
      <div className="flex flex-col pt-4 pb-8">
        <Input
          name="search"
          type="text"
          placeholder="Search Pollinator"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <SupportSearchFormResults
        query={searchQuery}
        results={results}
        onResultClick={onResultClick}
      />
    </Dialog>
  );
}

export default SupportSearchForm;

